<template>
</template>

<script>
  import _ from 'lodash'
  import Errors from '@/api/types/errors'
  import BidState from '@/api/types/bid-state'

  export default {
    computed: {
      bidStates() {
        return [
          {
            id: BidState.ACTIVE,
            name: 'bid-state.active',
            disabled: !this.bid.permissions.CAN_ACTIVATE_BID
          },
          {
            id: BidState.DECLINED,
            name: 'bid-state.declined',
            disabled: !this.bid.permissions.CAN_DECLINE_BID
          },
          {
            id: BidState.ACCEPTED,
            name: 'bid-state.accepted',
            disabled: !this.bid.permissions.CAN_ACCEPT_BID
          }
        ]
      }
    },

    data: () => ({
      saveWithError: true
    }),

    methods: {
      async saveForm() {
        if (this.$v) {
          this.$v.$touch();
          if (this.$v.$anyError && !this.saveWithError) return;
        }

        return this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('updatePublicLot', this.lot);
          }
          catch(e) {
            console.error('failed updating public lot', e);
            this.globalErrorHandler(e, {
              'INVALID_STATE': 'errors.public-lot.auto-save.request-already-sent',
              'ACCREDITATION_EXPIRED': 'errors.public-lot.auto-save.accreditation-expired',
              'INVALID_TYPE': 'errors.public-lot.auto-save.getting-request-period-finished',
              'INVALID_DATA': 'errors.public-lot.auto-save.cannot-update-data',
              'LOGIC_ERROR': 'errors.public-lot.auto-save.cannot-update-data',
              'ACCESS_DENIED': 'errors.public-lot.auto-save.wrong-state-for-new-data'
            }, 'errors.failed-updating');
          }
        });
      },

      async checkPublicLot() {
        await this.$store.dispatch('checkPublicLot', {
          lotId: this.lot.id
        });
      },

      async onBidSignCheckClick() {
        this.runAction(async () => {
          try {
            const signature = await this.$store.dispatch('checkBidSignature', {
              requestId: this.lot.request.id,
              bidId: this.bid.id
            });

            this.$refs.signCheck.open(signature);
          }
          catch(e) {
            console.error('failed checking bid signature', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async onAddBidClick() {
        this.runAction(async () => {
          try {
            const lot = await this.$store.dispatch('addNewBidToRequest', { requestId: this.lot.request.id });
            const newBid = lot.request.bids[lot.request.bids.length - 1];

            if (newBid) {
              this.$router.push({
                name: 'purchases-my-lots-details',
                params: {
                  lotId: this.lot.id,
                  tab: 'bid-' + newBid.id
                }
              })
            }
          }
          catch (e) {
            console.error('failed creating new bid', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onRemoveBidClick() {
        this.runAction(async () => {
          try {
            await this.$store.dispatch('removeBidFromRequest', { requestId: this.lot.request.id, bidId: this.bid.id });
          }
          catch (e) {
            console.error('failed removing bid', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onBidFileSignCheckClick(fileId) {
        this.runAction(async () => {
          try {
            const signature = await this.$store.dispatch('checkBidFileSignature', {
              requestId: this.lot.request.id,
              bidId: this.bid.num,
              fileId: fileId
            });

            this.$refs.signCheck.open(signature);
          }
          catch(e) {
            console.error('failed checking bid file signature', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async onBidFileSelected(data) {
        if (data instanceof File) {
          data = {
            file: data,
            sign: undefined
          }
        }

        this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('addFileToBid', {
              lotId: this.lot.id,
              requestId: this.lot.request.id,
              bidId: this.bid.num,
              file: data.file,
              sign: data.sign
            })
          }
          catch (e) {
            console.error('failed appending file to bid', e);
            this.globalErrorHandler(e, {
              'BAD_SIGNATURE': 'errors.public-lot.attaching-file-to-bid.bad-signature'
            },'errors.public-lot.attaching-file-to-bid.unknown');
          }
        })
      },

      async onRemoveBidFile(fileId) {
        this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('removeFileFromBid', {
              lotId: this.lot.id,
              requestId: this.lot.request.id,
              bidId: this.bid.num,
              fileId: fileId
            })
          }
          catch (e) {
            console.error('failed removing file from bid', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async onApproveBidClick(options) {
        this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('approveBid', {
              lotId: this.lot.id,
              requestId: this.lot.request.id,
              bidId: this.bid.num,
              verdict: options.verdict.valueOf(),
              reason: options.verdict == BidState.DECLINED ? options.reason : undefined,
              reglamentReason: options.verdict == this.enums.BidState.DECLINED || options.verdict == this.enums.BidState.CONDITIONALLY_APPROVED ? options.regulationsReason : undefined,
              acceptTime: options.verdict == BidState.ACCEPTED ? options.acceptTime : undefined,
            });
          }
          catch(e) {
            console.error('failed approving bid', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async onSaveCriterionEstimation(options) {
        this.runAction(async () => {
          try {
            await this.$store.dispatch('saveCriterionEstimation', {
              requestId: this.lot.request.id,
              criterionId: options.criterionId,
              bidId: this.bid.num,
              position: options.position,
              estimation: options.estimation
            });
          }
          catch(e) {
            console.error('failed saving criterion estimation', e);
            this.globalErrorHandler(e);
          }
        });
      },

      onEditBidForRetendClick() {
        this.$store.dispatch('enableEditBidForRetend', {
          lotId: this.lot.id,
          requestId: this.lot.request.id,
          bidId: this.bid.id,
          mode: true
        })
      },


      async doSignRetend() {
        return this.runBlockerAction(async () => {
          try {
            const text = await this.$store.dispatch('getTextForBidInRetend', {
              lot: this.lot,
              bid: this.bid
            });

            if (this.lot.permissions.SHOULD_BE_SIGNED) {
              this.$refs.signBid.open({text});
            }
            else {
              await this.signBid();
            }
          }
          catch(e) {
            console.error('failed sending changed bid for retend', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onSendBidForRetendClick() {
        // for retend in tender check if there are some positions whose cost became 0
        if (!this.lot.method.REDUCTION) {
          const zeroPriceItems = this.bid.specs.filter(spec => spec.price === 0 && spec.original && spec.original.price !== 0);
          const zeroDaysItems = this.bid.parts.filter(part => part.deliveryTime === 0 && part.original && part.original.deliveryTime !== 0);

          if (zeroPriceItems.length) {
            this.askConfirmation({
              title: 'Изменение предложения',
              body: 'ВНИМАНИЕ: Стоимость по некоторым позициям снизилась до нуля. Вы точно хотите зарегистрировать такое предложение?',
              onConfirm: () => this.doSignRetend()
            })
            return;
          }
          else if (zeroDaysItems.length) {
            this.askConfirmation({
              title: 'Изменение предложения',
              body: 'ВНИМАНИЕ: Срок поставки по некоторым партиям снизилась до нуля. Вы точно хотите зарегистрировать такое предложение?',
              onConfirm: () => this.doSignRetend()
            })
            return;
          }
        }

        return this.doSignRetend();
      },

      async onDownloadAllFilesClick() {
        this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('downloadAllFilesFromBid', {
              requestId: this.lot.request.id,
              bidNum: this.bid.num
            });
          }
          catch(e) {
            console.error('failed downloading all files from bid', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async signBid(result) {
        this.runBlockerAction(async () => {
          try {
            this.$refs.signBid.close();

            const lot = await this.$store.dispatch('signBidInRetend', {
              lot: this.lot,
              bid: this.bid,
              text: result ? result.data : undefined,
              signature: result ? result.signature : undefined
            });

            const bid = _.find(lot.request.bids, { num: this.bid.num });

            if (bid) {
              this.$router.replace({
                name: 'purchases-my-lots-details',
                params: {
                  lotId: this.lot.id,
                  tab: 'bid-' + bid.id
                }
              })
            }
          }
          catch(e) {
            console.error('failed signing changed bid for retend', e);

            this.globalErrorHandler(e, {
              'BAD_SIGNATURE': 'errors.public-lot.sending-bid-in-retend.bad-signature',
              'INVALID_STATE': 'errors.public-lot.sending-bid-in-retend.retend-finished',
              'ACCESS_DENIED': 'errors.public-lot.sending-bid-in-retend.access-denied',
              'INVALID_DATA': 'errors.public-lot.sending-bid-in-retend.cannot-update-data',
              'EXCEEDED_PRICE_LOW_THRESHOLD': 'errors.public-lot.sending-bid-in-retend.cost-exceeded-low-threshold"',
              'NOT_FILLED': 'errors.public-lot.sending-bid-in-retend.bid-not-filled',
              'NOT_FOUND': 'errors.public-lot.sending-bid-in-retend.bid-not-found',
              'BAD_PAYMENTS': 'errors.public-lot.sending-bid-in-retend.payments-are-worse',
              'BAD_PAYMENT_CONDITIONS': 'errors.public-lot.sending-bid-in-retend.payment-conditions-are-worse',
              'BAD_SPECIFICATION': 'errors.public-lot.sending-bid-in-retend.specification-is-worse',
              'BID_WORSE': 'errors.public-lot.sending-bid-in-retend.bid-is-worse',
              'INVALID_BLACK_BOX': 'errors.public-lot.common.invalid-black-box'
            }, 'errors.public-lot.sending-request.unknown');
          }
        });
      }
    }
  }
</script>

<style scoped>

</style>
